import { reportClientDiagnostic } from './clientDiagnostics';

/**
 * Time-series performance sampler.
 *
 * The threshold observers in clientDiagnostics.ts each fire on a *single* event
 * (one slow load, one long task). They cannot see the dashboard's actual reported
 * symptom — "the UI gets progressively laggier the longer the tab stays open."
 * That is *degradation over time*, and a single heap snapshot or one perf trace
 * can never reveal it (the 2026-06-08 animation-burn fix explicitly left the
 * "capture a 2nd snapshot after 20min and diff" step undone).
 *
 * This module records a rolling trend of the few signals that actually separate
 * the candidate root-causes:
 *   - usedJSHeapSize climbing monotonically  → memory leak / unbounded array
 *   - DOM-node count climbing                → list/log growth raising per-frame
 *                                              style-recalc cost (the suspected
 *                                              "worse after a while" amplifier)
 *   - long-task load rising with FLAT heap   → render/paint burn, not a leak
 *   - listeners / timers net-growing         → subscription/interval leak on remount
 *
 * Two modes:
 *   1. Always-on background sampling (every SAMPLE_MS) for ALL sessions. Bounded
 *      ring buffer, near-zero cost. Emits a trend snapshot to the existing
 *      diagnostics sink every few minutes and a `perf_trend_regression` event the
 *      moment a series shows sustained monotonic growth — so we catch the climb on
 *      the *real deployed app* during the long sessions where the lag actually bites.
 *   2. Opt-in on-screen overlay (`?perf=1`, persisted) you watch live while using
 *      the site. Never shown to ordinary users (investors/partners) — strictly opt-in.
 *
 * Lives outside React (vanilla DOM) so a re-render can never tear it down, matching
 * the visibility-gating pattern already in index.tsx.
 */

interface Sample {
  t: number;            // ms since first sample (session-relative)
  heapMB: number | null;
  domNodes: number;
  longTaskMs: number;   // total main-thread blocking time since previous sample
  longTaskCount: number;
}

const SAMPLE_MS = 20_000;            // background cadence (all users)
const SNAPSHOT_EVERY = 15;           // emit a trend snapshot every Nth sample (~5 min)
const MAX_SAMPLES = 1_080;           // ~6h @ 20s; bounded so the sampler can't itself leak
const REGRESSION_MIN_SPAN_MIN = 10;  // need a real window before trusting a slope
const REGRESSION_EMIT_COOLDOWN_MS = 10 * 60_000;

// Growth thresholds that warrant a "this is climbing" alert.
const HEAP_SLOPE_MB_PER_MIN = 1.5;   // ~+45MB over 30min sustained
const DOM_SLOPE_NODES_PER_MIN = 40;  // ~+1200 nodes over 30min sustained

const samples: Sample[] = [];
let sampleTick = 0;
let sessionStart = 0;
let lastRegressionEmit = 0;

// Long-task load accumulated between background samples (counts ALL long tasks,
// not just the >300ms ones clientDiagnostics reports).
let longTaskMsAccum = 0;
let longTaskCountAccum = 0;

// Overlay-only instrumentation (installed lazily; counts are approximate trends).
let instrumented = false;
let listenerNet = 0;
let timerNet = 0;
let fps = 0;
let rafFrames = 0;
let rafLastT = 0;
let rafId = 0;

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function readHeapMB(): number | null {
  const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
  if (!mem || typeof mem.usedJSHeapSize !== 'number') return null; // Chrome/Edge only
  return Math.round((mem.usedJSHeapSize / (1024 * 1024)) * 10) / 10;
}

function readHeapLimitMB(): number | null {
  const mem = (performance as unknown as { memory?: { jsHeapSizeLimit: number } }).memory;
  if (!mem || typeof mem.jsHeapSizeLimit !== 'number') return null;
  return Math.round(mem.jsHeapSizeLimit / (1024 * 1024));
}

/** Least-squares slope (units per MINUTE) plus endpoints, over the sampled series. */
function trend(pick: (s: Sample) => number | null): {
  slopePerMin: number; first: number; last: number; spanMin: number; n: number;
} | null {
  const pts: Array<{ x: number; y: number }> = [];
  for (const s of samples) {
    const y = pick(s);
    if (y === null || Number.isNaN(y)) continue;
    pts.push({ x: s.t / 60_000, y }); // x in minutes
  }
  if (pts.length < 3) return null;
  const n = pts.length;
  const sx = pts.reduce((a, p) => a + p.x, 0);
  const sy = pts.reduce((a, p) => a + p.y, 0);
  const sxx = pts.reduce((a, p) => a + p.x * p.x, 0);
  const sxy = pts.reduce((a, p) => a + p.x * p.y, 0);
  const denom = n * sxx - sx * sx;
  const slope = denom === 0 ? 0 : (n * sxy - sx * sy) / denom;
  return {
    slopePerMin: slope,
    first: pts[0].y,
    last: pts[pts.length - 1].y,
    spanMin: pts[pts.length - 1].x - pts[0].x,
    n,
  };
}

function checkRegression(): void {
  const t = Date.now();
  if (t - lastRegressionEmit < REGRESSION_EMIT_COOLDOWN_MS) return;

  const heap = trend((s) => s.heapMB);
  const dom = trend((s) => s.domNodes);
  const span = Math.max(heap?.spanMin ?? 0, dom?.spanMin ?? 0);
  if (span < REGRESSION_MIN_SPAN_MIN) return;

  const heapClimb = heap && heap.slopePerMin > HEAP_SLOPE_MB_PER_MIN;
  const domClimb = dom && dom.slopePerMin > DOM_SLOPE_NODES_PER_MIN;
  if (!heapClimb && !domClimb) return;

  lastRegressionEmit = t;
  void reportClientDiagnostic(
    'perf_trend_regression',
    {
      heap: heap && {
        slopeMBPerMin: Math.round(heap.slopePerMin * 100) / 100,
        firstMB: heap.first, lastMB: heap.last, spanMin: Math.round(heap.spanMin),
      },
      dom: dom && {
        slopeNodesPerMin: Math.round(dom.slopePerMin),
        first: dom.first, last: dom.last, spanMin: Math.round(dom.spanMin),
      },
      climbing: [heapClimb ? 'heap' : null, domClimb ? 'dom' : null].filter(Boolean),
      samples: samples.length,
    },
    `Sustained resource growth over ${Math.round(span)}min — ` +
      `${heapClimb ? `heap +${heap!.slopePerMin.toFixed(1)}MB/min ` : ''}` +
      `${domClimb ? `dom +${Math.round(dom!.slopePerMin)} nodes/min` : ''}`.trim(),
  );
}

function emitSnapshot(): void {
  const heap = trend((s) => s.heapMB);
  const dom = trend((s) => s.domNodes);
  const recent = samples.slice(-SNAPSHOT_EVERY);
  const ltMs = recent.reduce((a, s) => a + s.longTaskMs, 0);
  const ltCount = recent.reduce((a, s) => a + s.longTaskCount, 0);
  void reportClientDiagnostic(
    'perf_sample_snapshot',
    {
      heapMB: heap?.last ?? null,
      heapSlopeMBPerMin: heap ? Math.round(heap.slopePerMin * 100) / 100 : null,
      domNodes: dom?.last ?? null,
      domSlopeNodesPerMin: dom ? Math.round(dom.slopePerMin) : null,
      longTaskMsPerMin: Math.round((ltMs / (SNAPSHOT_EVERY * SAMPLE_MS / 60_000)) || 0),
      longTaskCountWindow: ltCount,
      spanMin: Math.round(heap?.spanMin ?? dom?.spanMin ?? 0),
      samples: samples.length,
    },
    'Periodic perf trend snapshot',
  );
}

function takeSample(): void {
  if (sessionStart === 0) sessionStart = nowMs();
  const s: Sample = {
    t: nowMs() - sessionStart,
    heapMB: readHeapMB(),
    domNodes: document.getElementsByTagName('*').length,
    longTaskMs: Math.round(longTaskMsAccum),
    longTaskCount: longTaskCountAccum,
  };
  longTaskMsAccum = 0;
  longTaskCountAccum = 0;

  samples.push(s);
  if (samples.length > MAX_SAMPLES) samples.shift();

  sampleTick += 1;
  checkRegression();
  if (sampleTick % SNAPSHOT_EVERY === 0) emitSnapshot();
}

function observeAllLongTasks(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const po = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        longTaskMsAccum += entry.duration;
        longTaskCountAccum += 1;
      }
    });
    po.observe({ type: 'longtask', buffered: false });
  } catch {
    // longtask not supported (Firefox/Safari) — heap & DOM trends still work.
  }
}

// ---------------------------------------------------------------------------
// Opt-in live overlay (you turn it on with ?perf=1; never shown to others)
// ---------------------------------------------------------------------------

function overlayEnabled(): boolean {
  try {
    const url = new URL(window.location.href);
    const q = url.searchParams.get('perf');
    if (q === '1' || q === 'on') localStorage.setItem('boc:perf', '1');
    if (q === '0' || q === 'off') localStorage.removeItem('boc:perf');
    return localStorage.getItem('boc:perf') === '1';
  } catch {
    return false;
  }
}

/**
 * Patch addEventListener/removeEventListener + timers to maintain NET counters.
 * Pass-through wrappers (identical args → identical semantics), guarded so a
 * counting bug can never break the wrapped call. Overlay-only: not worth the
 * (tiny) global indirection for ordinary sessions. Counts are trend signals,
 * not exact totals (listeners added before patch aren't counted).
 */
function instrumentOnce(): void {
  if (instrumented) return;
  instrumented = true;
  try {
    const ep = EventTarget.prototype;
    const origAdd = ep.addEventListener;
    const origRemove = ep.removeEventListener;
    ep.addEventListener = function (this: EventTarget, ...args: Parameters<typeof origAdd>) {
      try { listenerNet += 1; } catch { /* counting must never break the app */ }
      return origAdd.apply(this, args);
    };
    ep.removeEventListener = function (this: EventTarget, ...args: Parameters<typeof origRemove>) {
      try { listenerNet -= 1; } catch { /* noop */ }
      return origRemove.apply(this, args);
    };

    const w = window as unknown as Record<string, (...a: unknown[]) => unknown>;
    const origSI = w.setInterval;
    const origCI = w.clearInterval;
    w.setInterval = function (this: unknown, ...args: unknown[]) {
      try { timerNet += 1; } catch { /* noop */ }
      return origSI.apply(this, args);
    };
    w.clearInterval = function (this: unknown, ...args: unknown[]) {
      try { timerNet -= 1; } catch { /* noop */ }
      return origCI.apply(this, args);
    };
  } catch {
    // If patching is blocked, the overlay simply shows listener/timer as n/a.
  }
}

function startFpsMeter(): void {
  rafLastT = nowMs();
  const loop = () => {
    rafFrames += 1;
    const now = nowMs();
    if (now - rafLastT >= 1000) {
      fps = Math.round((rafFrames * 1000) / (now - rafLastT));
      rafFrames = 0;
      rafLastT = now;
    }
    rafId = requestAnimationFrame(loop);
  };
  rafId = requestAnimationFrame(loop);
}

const SPARK = '▁▂▃▄▅▆▇█';
function sparkline(values: number[], width = 24): string {
  const v = values.slice(-width);
  if (v.length === 0) return '';
  const min = Math.min(...v);
  const max = Math.max(...v);
  const range = max - min || 1;
  return v.map((y) => SPARK[Math.min(SPARK.length - 1, Math.floor(((y - min) / range) * (SPARK.length - 1)))]).join('');
}

function arrow(slopePerMin: number, eps: number): string {
  if (slopePerMin > eps) return '↑';
  if (slopePerMin < -eps) return '↓';
  return '→';
}

function buildOverlay(): HTMLDivElement {
  const el = document.createElement('div');
  el.id = 'boc-perf-overlay';
  Object.assign(el.style, {
    position: 'fixed', bottom: '8px', left: '8px', zIndex: '2147483647',
    font: '11px/1.45 ui-monospace,SFMono-Regular,Menlo,monospace',
    color: '#d8ffe0', background: 'rgba(8,16,12,0.88)', border: '1px solid #1f5c3a',
    borderRadius: '8px', padding: '8px 10px', minWidth: '230px',
    boxShadow: '0 4px 18px rgba(0,0,0,0.45)', pointerEvents: 'auto', userSelect: 'text',
    backdropFilter: 'blur(2px)',
  } as CSSStyleDeclaration);
  el.innerHTML =
    '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px">' +
    '<b style="color:#ffd166">BOC perf</b>' +
    '<span><a href="#" id="boc-perf-dump" style="color:#7fd1ff;margin-right:8px;text-decoration:none">dump</a>' +
    '<a href="#" id="boc-perf-close" style="color:#ff8a8a;text-decoration:none">×</a></span></div>' +
    '<div id="boc-perf-body"></div>';
  return el;
}

function renderOverlay(body: HTMLElement): void {
  const heap = trend((s) => s.heapMB);
  const dom = trend((s) => s.domNodes);
  const heapVals = samples.map((s) => s.heapMB).filter((x): x is number => x !== null);
  const domVals = samples.map((s) => s.domNodes);
  const recent = samples.slice(-SNAPSHOT_EVERY);
  const ltMsPerMin = Math.round(
    (recent.reduce((a, s) => a + s.longTaskMs, 0) / (recent.length * SAMPLE_MS / 60_000)) || 0,
  );
  const heapLimit = readHeapLimitMB();
  const spanMin = Math.round(heap?.spanMin ?? dom?.spanMin ?? 0);

  const row = (label: string, value: string, spark = '', warn = false) =>
    `<div style="display:flex;justify-content:space-between;gap:10px${warn ? ';color:#ff9d6e' : ''}">` +
    `<span style="opacity:.7">${label}</span><span style="text-align:right">${spark ? `<span style="opacity:.5">${spark}</span> ` : ''}${value}</span></div>`;

  const fpsWarn = fps > 0 && fps < 45;
  const heapWarn = !!heap && heap.slopePerMin > HEAP_SLOPE_MB_PER_MIN;
  const domWarn = !!dom && dom.slopePerMin > DOM_SLOPE_NODES_PER_MIN;

  body.innerHTML =
    row('fps', fps ? String(fps) : '…', '', fpsWarn) +
    row('heap', heap
      ? `${heap.last}${heapLimit ? `/${heapLimit}` : ''}MB ${arrow(heap.slopePerMin, 0.3)}${heap.slopePerMin > 0.3 ? ` +${heap.slopePerMin.toFixed(1)}/min` : ''}`
      : 'n/a (non-Chrome)', sparkline(heapVals), heapWarn) +
    row('dom', dom
      ? `${dom.last} ${arrow(dom.slopePerMin, 5)}${dom.slopePerMin > 5 ? ` +${Math.round(dom.slopePerMin)}/min` : ''}`
      : String(document.getElementsByTagName('*').length), sparkline(domVals), domWarn) +
    row('longtask', `${ltMsPerMin}ms/min`, '', ltMsPerMin > 1500) +
    row('listeners', instrumented ? `${listenerNet >= 0 ? '+' : ''}${listenerNet} net` : 'n/a') +
    row('timers', instrumented ? `${timerNet} active` : 'n/a') +
    row('session', `${spanMin}min · ${samples.length} samples`);
}

function dumpToConsole(): void {
  const heap = trend((s) => s.heapMB);
  const dom = trend((s) => s.domNodes);
  // eslint-disable-next-line no-console
  console.log('%c[BOC perf] session trend', 'color:#ffd166;font-weight:bold', {
    heap: heap && { firstMB: heap.first, lastMB: heap.last, slopeMBPerMin: +heap.slopePerMin.toFixed(2), spanMin: +heap.spanMin.toFixed(1) },
    dom: dom && { first: dom.first, last: dom.last, slopeNodesPerMin: Math.round(dom.slopePerMin), spanMin: +dom.spanMin.toFixed(1) },
    samples,
  });
  // eslint-disable-next-line no-console
  console.table(samples.map((s) => ({ min: +(s.t / 60_000).toFixed(1), heapMB: s.heapMB, domNodes: s.domNodes, longTaskMs: s.longTaskMs })));
}

function mountOverlay(): void {
  instrumentOnce();
  startFpsMeter();
  const el = buildOverlay();
  document.body.appendChild(el);
  const body = el.querySelector<HTMLElement>('#boc-perf-body')!;
  // First paint immediately so it doesn't show empty for a second.
  takeSample();
  renderOverlay(body);
  const ui = window.setInterval(() => renderOverlay(body), 1000);

  el.querySelector('#boc-perf-close')!.addEventListener('click', (e) => {
    e.preventDefault();
    window.clearInterval(ui);
    if (rafId) cancelAnimationFrame(rafId);
    el.remove();
    try { localStorage.removeItem('boc:perf'); } catch { /* noop */ }
  });
  el.querySelector('#boc-perf-dump')!.addEventListener('click', (e) => { e.preventDefault(); dumpToConsole(); });
}

/** Install the time-series sampler. Call once at app boot. */
export function installPerfSampler(): void {
  if (typeof window === 'undefined') return;

  observeAllLongTasks();
  sessionStart = nowMs();
  window.setInterval(takeSample, SAMPLE_MS);

  // Console handle so you can pull the raw series at any time: window.__bocPerf.dump()
  (window as unknown as { __bocPerf?: unknown }).__bocPerf = {
    dump: dumpToConsole,
    samples: () => samples.slice(),
    enable: () => { try { localStorage.setItem('boc:perf', '1'); } catch { /* noop */ } if (!document.getElementById('boc-perf-overlay')) mountOverlay(); },
    disable: () => { try { localStorage.removeItem('boc:perf'); } catch { /* noop */ } document.getElementById('boc-perf-overlay')?.remove(); },
  };

  if (overlayEnabled()) {
    if (document.body) mountOverlay();
    else window.addEventListener('DOMContentLoaded', mountOverlay, { once: true });
  }
}
